// Libraries
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

// Dependencies
import { LAYOUT_LIMIT_WIDTH } from 'components/elements/Form';
import parsePhoneNumber from 'libphonenumber-js';
import Customization from 'models/Customization';
import MentorModel from 'models/Mentor';
import TranslationsContext from 'providers/translationsProvider';
import { useListPublicCountriesQuery } from 'services/apiSlices/countriesApi';
import { useLoginMutation } from 'services/apiSlices/oauth2Api';
import B4Blogo from 'static/images/B4Blogo.svg';
import { getCookie } from 'utils/analytics';
import isMobile from 'utils/isMobile';
import languageDetector, { LANGUAGE_OPTIONS } from 'utils/languageDetector';
import passwordValidator from 'utils/passwordValidator';
import queryString from 'utils/queryString';
import {
  COMM_FALSE,
  COMM_TRUE,
  getPrivacyPolicyByLang,
  getTermsAndConditionsByLang,
  INPUT_COMMUNICATION,
  INPUT_CONFIRM_PASS,
  INPUT_CURRENT_COUNTRY,
  INPUT_EMAIL,
  INPUT_LANGUAGE,
  INPUT_PASS,
  INPUT_PHONE,
  INPUT_ROLE,
  ROLE_MEMBER,
  ROLE_MENTOR,
} from './utils';

// Actions
import { actions as translationActions } from 'store/modules/translations';
import { actions as authActions, selectors as userSelectors } from 'store/modules/user';

// Components
import { Col, Popover, Row, Space } from 'antd';
import {
  Button,
  CountrySelect,
  Form,
  FullNameItem,
  Input,
  Link,
  Notification,
  Panel,
  PasswordInput,
  PhoneNumberInput,
  Radio,
  Select,
} from 'components/elements';
import PasswordChecklist from 'components/elements/PasswordChecklist';
import { H1, P } from 'components/fontSystem';
import ForgotPasswordModal from 'containers/LoginForm/ForgotPassword';
import SecurityText from 'containers/LoginForm/ForgotPassword/SecurityStatement';
import RegistrationErrorModal from './RegistrationErrorModal';

const RegisterForm = ({
  alignUserData,
  bottomImg = null,
  bottomLayout = LAYOUT_LIMIT_WIDTH,
  disableMember = false,
  disableMentor = false,
  logoImg = null,
  logoLayout = LAYOUT_LIMIT_WIDTH,
  onAuth = null,
  programName = null,
  mentorPhoneNumberRequiredOnSignup = false,
  memberPhoneNumberRequiredOnSignup = false,
  isCustomLink,
  customization,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { t, format } = useContext(TranslationsContext);

  const [resetModalVisible, setResetModalVisible] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [language, setLanguage] = useState(languageDetector());
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
  const [errors, setErrors] = useState(false);

  const hasCustomization = !!customization.id;

  const formValues = Form.useWatch([], form);
  const password = formValues?.password;
  const email = formValues?.email;
  const name = formValues?.first_name;
  const lastname = formValues?.last_name;
  const userAttributes = { email, name, lastname };

  // Selectors
  const fetchingUser = useSelector(userSelectors.fetchingUser);

  // Actions
  const { clearError, userRegister } = authActions;
  const { translationsChange } = translationActions;

  const [login] = useLoginMutation();

  const role = Form.useWatch(INPUT_ROLE, form);
  const isPhoneNumberRequired =
    // if it's a general link
    !isCustomLink ||
    // or if it is required depending on the role
    (memberPhoneNumberRequiredOnSignup && ROLE_MEMBER === role) ||
    (mentorPhoneNumberRequiredOnSignup && ROLE_MENTOR === role);

  const getQueryParams = () => queryString.parse(document.location.search);
  const { organization, program } = getQueryParams();

  const loginParams =
    organization && program ? `?organization=${organization}&program=${program}` : '';

  useEffect(() => {
    dispatch(translationsChange({ selectedLanguage: language }));
  }, [language]);

  const handleOnSelectLanguage = value => {
    setLanguage(value);
    dispatch(translationsChange({ selectedLanguage: value }));
  };

  const onFormChange = () => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setIsSubmitEnabled(true))
      .catch(() => setIsSubmitEnabled(false));

    alignUserData(form.getFieldsValue());
  };

  // Debounce and memoize the onValuesChange event callback
  const debouncedOnFormChange = useMemo(() => debounce(onFormChange, 1000), []);

  const handleOnFinish = values => {
    setIsSubmitted(true);

    const lowerCaseEmail = String(values.email).toLowerCase();
    const userType = values.role;
    const userData = {
      first_name: values.first_name,
      last_name: values.last_name,
      email: lowerCaseEmail,
      status: MentorModel.STATUS_APP_INCOMPLETE,
      language: values.language,
      country: values.country,
      emails: values.emails,
    };
    if (getCookie('ruid')) {
      userData.ruid = getCookie('ruid');
    }
    if (getCookie('hubspotutk')) {
      userData.hubspotutk = getCookie('hubspotutk');
    }
    if (isPhoneNumberRequired) {
      userData.phone_number = values.phone_number;
    }
    const data = {
      username: lowerCaseEmail,
      password: values.password,
      re_password: values.re_password,
      [userType]: userData,
    };
    if (organization && program) {
      data.organization = organization;
      data.program = program;
    }

    dispatch(
      userRegister({
        ...data,
        callback: responseErrors => {
          if (responseErrors) {
            setErrors(responseErrors);
            return;
          }

          login({
            username: lowerCaseEmail,
            password: values.password,
          })
            .unwrap()
            .then(onAuth)
            .catch(() => {});
        },
      })
    );
  };

  const handleModalClose = () => {
    setIsSubmitted(false);
    dispatch(clearError());
  };

  const handleCloseResetModal = () => {
    setResetModalVisible(false);
  };

  const handleOpenResetModal = () => {
    setErrors(false);
    setResetModalVisible(true);
  };

  const handleSearchSelectClick = inputName => {
    if (isMobile) {
      const label = document.querySelector(`[for="${inputName}"]`);
      label.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  };

  const infoContent =
    t(`Entrepreneur - Join as an entrepreneur if you plan to apply to one of our incubation programs.
      Mentor - Join as a mentor if you’re interested in mentoring entrepreneurs.
      All members are part of our digital ecosystem and network.
    `);

  const phoneNumberInfo = t(
    'We will only use your number to support you through the application process and for impact tracking. Please provide the number that is associated with your messaging applications (WhatsApp, Telegram, Line, etc.) We will never share it with anyone or use it for promotional purposes.'
  );

  const valueDoesntExist = value =>
    value === undefined || value === null || value === '' || value === '<p></p>';

  const labeledJoinQuestion = hasCustomization
    ? t('I want to join the incubation program as...')
    : t('I want to join Bridge for Billions as...');

  const manageCommQuestion = () => {
    if (hasCustomization && !valueDoesntExist(customization.communication_question)) {
      // We are returning a JSX node with inner HTML to accept and manage whatever comes from the rich editor
      return (
        <div
          style={{ display: 'inline-block' }}
          dangerouslySetInnerHTML={{
            __html: customization.communication_question,
          }}
        />
      );
    }
    return t(
      'Would you like us to let you know about any updates, news, events and opportunities?'
    );
  };

  const receiveInfo = () => {
    if (hasCustomization && !valueDoesntExist(customization.communication_answer_true)) {
      return customization.communication_answer_true;
    }
    return t('Yes, I definitely want to be up to date.');
  };

  const notReceiveInfo = () => {
    if (hasCustomization && !valueDoesntExist(customization.communication_answer_false)) {
      return customization.communication_answer_false;
    }
    return t('Not for now, thank you.');
  };

  const regularDisclaimer = format(
    t(
      'By creating your account, you agree to <a href=%(termAndCondLink)s target="_blank">Bridge for Billions’ Terms and Conditions</a> & <a href=%(policyLink)s target="_blank">Privacy Policy</a>.'
    ),
    {
      termAndCondLink: getTermsAndConditionsByLang(language),
      policyLink: getPrivacyPolicyByLang(language),
    }
  );

  const countriesData = useListPublicCountriesQuery().data;

  const formHeaderLogo = hasCustomization
    ? { img: logoImg, alt: t('Program logo') }
    : { img: B4Blogo, alt: 'Bridge for Billions' };

  return (
    <CustomPanel $customLink={isCustomLink}>
      <Notification />
      <Form
        form={form}
        onFinish={handleOnFinish}
        onValuesChange={debouncedOnFormChange}
        className="white-bg"
        layout="vertical"
      >
        <Form.Header
          imgSrc={formHeaderLogo.img}
          imgAlt={formHeaderLogo.alt}
          $logoLayout={logoLayout}
        />
        <Row type="flex" justify="space-between">
          <Col sm={24} xs={24} md={24}>
            <H1>{programName ?? t('Create your account to get started')}</H1>
          </Col>
          <Col>
            <Form.Item
              name={INPUT_LANGUAGE}
              initialValue={language}
              size="large"
              className="line-height-1"
            >
              <Select
                data-test-id="signup-select-languages"
                onSelect={handleOnSelectLanguage}
                options={LANGUAGE_OPTIONS}
                popupMatchSelectWidth={false}
                className="languages-select"
                popupClassName="languages-menu"
                bordered
              />
            </Form.Item>
          </Col>
        </Row>
        <P style={{ paddingBottom: '15px' }}>
          {/* TODO: "Login here" should be one not-separated block, otherwise could cause issues in some translations.  */}
          {t('Already a Member? Login')}
          &nbsp;
          <Link
            to={{
              pathname: '/login',
              search: loginParams,
            }}
          >
            {t('here')}
          </Link>
          .
        </P>
        <FullNameItem form={form} />
        <Form.Item
          label={t('Email')}
          size="large"
          rules={[
            { required: true, message: t('Enter your email here') },
            { type: 'email', message: t('Please enter a valid email') },
          ]}
          name={INPUT_EMAIL}
        >
          <Input placeholder={t('Your email')} />
        </Form.Item>

        <Popover
          content={<PasswordChecklist password={password} userAttributes={userAttributes} t={t} />}
          trigger="focus"
        >
          <Form.Item
            label={t('Password (min. 10 characters)')}
            size="large"
            name={INPUT_PASS}
            rules={[{ validator: passwordValidator(t, userAttributes) }]}
          >
            <PasswordInput hidePopover placeholder={t('Create your password')} />
          </Form.Item>
        </Popover>

        <Form.Item
          noAsterisk
          label={t('Confirm password')}
          size="large"
          name={INPUT_CONFIRM_PASS}
          dependencies={[INPUT_PASS]} // Validate only if INPUT_PASS changes
          displayBlock
          rules={[
            { required: true, message: t('Enter your password here') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue(INPUT_PASS) === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('Passwords do not match!')));
              },
            }),
          ]}
        >
          <PasswordInput hidePopover placeholder={t('Enter your password again')} />
        </Form.Item>

        <Row>
          <Form.Item
            align={{
              offset: [-5, 10],
              targetOffset: ['50%', '40%'],
            }}
            infotext={infoContent}
            label={labeledJoinQuestion}
            name={INPUT_ROLE}
            placement="bottomLeft"
            size="large"
            rules={[{ required: true, message: t('Please select your role!') }]}
          >
            <Radio.Group size="large">
              <Space direction="vertical">
                <Radio value={ROLE_MEMBER} disabled={disableMember}>
                  {t('an entrepreneur.')}
                </Radio>
                <Radio value={ROLE_MENTOR} disabled={disableMentor}>
                  {t('a volunteer mentor for entrepreneurs.')}
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        </Row>
        <Row>
          <Col md={12} sm={24}>
            <Form.Item
              label={t('Current Country')}
              size="large"
              rules={[
                {
                  required: true,
                  message: t('Please select your current country'),
                },
              ]}
              name={INPUT_CURRENT_COUNTRY}
            >
              <CountrySelect
                countriesData={countriesData}
                t={t}
                onFocus={() => handleSearchSelectClick(INPUT_CURRENT_COUNTRY)}
              />
            </Form.Item>
          </Col>
        </Row>

        {isPhoneNumberRequired && (
          <Row>
            <Col md={12} sm={24}>
              <Form.Item
                label={t('Your phone number')}
                infotext={phoneNumberInfo}
                size="large"
                name={INPUT_PHONE}
                isRequired
                rules={[
                  {
                    validator: (_, value) =>
                      value && parsePhoneNumber(value)?.isValid()
                        ? Promise.resolve()
                        : Promise.reject(new Error(t('The phone number entered is not valid.'))),
                  },
                ]}
              >
                <PhoneNumberInput />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Row style={{ marginTop: '20px' }}>
          <Form.Item
            label={manageCommQuestion()}
            name={INPUT_COMMUNICATION}
            size="large"
            rules={[{ required: true, message: t('Please select one option!') }]}
          >
            <Radio.Group>
              <Space direction="vertical">
                <Radio value={COMM_TRUE}>{receiveInfo()}</Radio>
                <Radio value={COMM_FALSE}>{notReceiveInfo()}</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        </Row>

        <Row type="flex">
          <Col xs={24} sm={24} md={8} xl={6} xxl={5}>
            <Form.Item size="large">
              <Button
                disabled={!isSubmitEnabled}
                htmlType="submit"
                loading={fetchingUser || isSubmitted}
                size="large"
                style={{ marginTop: '8px' }}
                type="primary"
              >
                {t("LET'S GO")}
              </Button>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={24} xl={24} xxl={24}>
            <P
              style={{
                fontSize: '14px',
                fontWeight: 400,
                fontStyle: 'italic',
                marginTop: '10px',
                marginBottom: '10px',
              }}
              dangerouslySetInnerHTML={{ __html: regularDisclaimer }}
            />
            {hasCustomization && customization.presubmit_message && (
              <P
                style={{
                  fontSize: '14px',
                  fontWeight: 400,
                  fontStyle: 'italic',
                }}
                dangerouslySetInnerHTML={{
                  __html: customization.presubmit_message,
                }}
              />
            )}
          </Col>
        </Row>
      </Form>

      <RegistrationErrorModal
        errors={errors}
        loginParams={loginParams}
        onCancel={() => {
          setErrors(false);
          handleModalClose();
        }}
        onOpenResetModal={handleOpenResetModal}
      />

      <SecurityText />
      <ForgotPasswordModal visible={resetModalVisible} onCancel={handleCloseResetModal} />
      <Form.FooterImg src={bottomImg} $logoLayout={bottomLayout} />
    </CustomPanel>
  );
};

const CustomPanel = styled(Panel)`
  overflow-x: ${({ $customLink }) => ($customLink ? 'inherit' : 'hidden')};
  overflow-y: ${({ $customLink }) => ($customLink ? 'inherit' : 'scroll')};
  height: ${({ $customLink }) => ($customLink ? '100%' : 'calc(100vh - 60px)')};
`;

RegisterForm.propTypes = {
  bottomImg: PropTypes.string,
  bottomLayout: PropTypes.number,
  disableMember: PropTypes.bool,
  disableMentor: PropTypes.bool,
  logoImg: PropTypes.string,
  logoLayout: PropTypes.number,
  onAuth: PropTypes.func,
  programName: PropTypes.string,
  mentorPhoneNumberRequiredOnSignup: PropTypes.bool,
  memberPhoneNumberRequiredOnSignup: PropTypes.bool,
  alignUserData: PropTypes.func.isRequired,
  isCustomLink: PropTypes.bool.isRequired,
  customization: PropTypes.instanceOf(Customization).isRequired,
};

export default RegisterForm;
